/**
 * The sub board's Firestore query, in ONE place, so the rules suite can
 * prove the query the app actually sends.
 *
 * WHY THIS FILE EXISTS. Since 2026-08-06 the subRequests read rule gates on
 * a stored `tier` field, which puts the board in the unprovable-LIST family
 * docs/lessons.md says has bitten this repo six times: Firestore refuses a
 * query it cannot prove rather than narrowing it, so a missing or wrong
 * where() clause does not surface as an error on screen. It surfaces as an
 * EMPTY BOARD, which reads as "nobody needs cover" and is worse than the
 * over-sharing the tier scoping replaced.
 *
 * A test that re-types the query by hand cannot catch that: it proves a
 * query nobody ships. hub/tests/firestore-rules.test.mjs imports THIS
 * builder and runs it against the emulator for every role, so the thing
 * proven and the thing shipped are the same code.
 *
 * Keep this module free of React and of anything that reaches for a live
 * Firebase app, so it stays importable from a plain node test.
 */
import { studioTodayKey } from './practiceSummary.ts';
import {
  collection,
  orderBy,
  query,
  where,
  type DocumentData,
  type Firestore,
  type Query,
  type QueryFieldFilterConstraint,
} from 'firebase/firestore';

/**
 * THE SR STAFF BOARD: the tiers that share one board.
 *
 * Annie, an Executive Teacher, 2026-09-28, relayed by Bobbie: "I think there
 * may be an issue with the Sr Staff sub request board. Miss Sydney put a
 * request out there but I am not able to see it". A request's tier is its
 * filer's role, and an Executive Teacher read only her own tier, so anything
 * a Manager or Director filed never reached her. Asked whether all senior
 * staff should share one Sub Board, Bobbie chose the option labeled "One Sr
 * Staff board (Recommended)": "Executive Teachers, Managers and Directors
 * all see and can claim each other's sub requests. Jr Staff keep their own
 * board."
 *
 * Managers and Directors already read every tier (ALL_TIER_ROLES in
 * lib/subs.ts), so the only reader this list changes is the Executive
 * Teacher. Jr Staff are NOT on it: their board stays their own in both
 * directions. Kept in lockstep
 * with the subRequests read rule in shared/firestore.rules (its company
 * disjunct) and with netlify/functions/lib/subTierReach.mjs.
 *
 * Frozen and module-level so the value subTierFilterFor() hands a hook is
 * the SAME array every render; a fresh array each time would re-run the
 * listener effect on every render. Lives here rather than in lib/subs.ts so
 * hub/tests/firestore-rules.test.mjs imports and proves the shipped list.
 */
export const SR_STAFF_SUB_TIERS = Object.freeze([
  'company',
  'manager',
  'director',
] as const);

/**
 * What a sub board query filters `tier` on: one tier, a set of tiers that
 * share one board, or null for no tier constraint at all. Produced by
 * subTierFilterFor() in lib/subs.ts and nowhere else.
 */
export type SubTierFilter = string | readonly string[] | null;

/**
 * The tier clause for a filtered board. One tier is `==`, exactly the shape
 * that has shipped since 2026-08-06; a shared board is `in`, which the rules
 * prove value by value (the subRequests read rule in shared/firestore.rules) and
 * which rides the same (tier ASC, date ASC) index.
 */
function subTierWhere(tierFilter: string | readonly string[]): QueryFieldFilterConstraint {
  return typeof tierFilter === 'string'
    ? where('tier', '==', tierFilter)
    : where('tier', 'in', [...tierFilter]);
}

/**
 * The lower bound the sub board queries from: the STUDIO's today.
 *
 * THE P1 THIS EXISTS TO CLOSE (Codex review of PR #1016). The bound used to be
 * assembled inline from new Date().getFullYear() and friends, which is the
 * DEVICE's calendar day. On any device whose local date runs ahead of Chicago,
 * a UTC runtime after 7pm Central being the everyday case, that bound is
 * TOMORROW in studio terms, so `date >= bound` discards today's requests inside
 * Firestore before any studio-keyed filter downstream ever sees them. The front
 * desk board then reads empty during exactly the evening window the rest of
 * this work was fixing, and an empty sub board reads as "nobody needs cover",
 * which is the failure mode this whole module was written to prevent.
 *
 * Delegates to studioTodayKey (lib/practiceSummary), the one America/Chicago
 * answer this app already had. The clock is a parameter so the boundary can be
 * tested at a chosen instant rather than at the runner's own timezone.
 */
export function subBoardFromDate(now = new Date()): string {
  try {
    return studioTodayKey(now);
  } catch {
    // GUARDED because this value is read in a useState initializer on the sub
    // board (SubRequests.tsx's todayString), where a throw is a white screen
    // rather than a wrong day. studioTodayKey calls Intl.DateTimeFormat with no
    // guard of its own, and a runtime with no time zone data throws there. Same
    // documented trade compDayVisible.ts already makes for the comp ticker:
    // being wrong by one zone beats showing nothing at all. Deliberately NOT
    // fixed inside studioTodayKey, which the dancer portal's own summary math
    // shares; changing its failure mode belongs with that lane, not this one.
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}

const DATE_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The first studio date a covered-class reader must still see: `days` studio
 * days before `todayKey`. The sub window stays open until 24 hours after the
 * class (lib/subClassGrant.mjs), so on the morning after she covers, the class
 * is still open to her; a reader built on `date >= today` would drop it while
 * her seat no longer lists it as weekly (feedback gxpVbdu6). Only the screens
 * that show HER covers widen by a day (Calendar, Teach); the sub board itself
 * keeps `date >= today`. Noon UTC, so no clock change can roll the date twice.
 * Returns todayKey unchanged for days <= 0 or a malformed key.
 */
export function subLookBackFromDate(todayKey: string, days: number): string {
  if (!(days > 0) || !DATE_KEY_RE.test(todayKey)) return todayKey;
  const [y, m, d] = todayKey.split('-').map(Number);
  const shifted = new Date(Date.UTC(y, m - 1, d - Math.floor(days), 12, 0, 0));
  return shifted.toISOString().slice(0, 10);
}

/**
 * True for a real YYYY-MM-DD calendar date, not just the right shape.
 *
 * Used to validate the `?date=` the Assistant's Sub Board destination
 * hands SubRequests.tsx (assistant.mjs's DESTINATIONS.sub_board), so a
 * malformed or out-of-range value never becomes the form's initial date.
 * The shape check alone is not enough: '2026-02-30' matches
 * `/^\d{4}-\d{2}-\d{2}$/` and JS's Date constructor rolls it forward into
 * March silently rather than rejecting it, which is exactly the kind of
 * quiet-wrong-day bug docs/lessons.md already has a whole file about.
 */
export function isValidDateKey(value: string | null | undefined): value is string {
  if (!value || !DATE_KEY_RE.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number);
  const parsed = new Date(Date.UTC(y, m - 1, d));
  return (
    parsed.getUTCFullYear() === y
    && parsed.getUTCMonth() === m - 1
    && parsed.getUTCDate() === d
  );
}

/**
 * The Admin desk shows tonight, and only tonight. A date parameter may still
 * seed the request form for other staff, but it cannot move the desk board.
 */
export function adminSubBoardDateKey(
  requested: string | null | undefined,
  today = subBoardFromDate()
): string {
  return isValidDateKey(requested) && requested === today ? requested : today;
}

/**
 * Upcoming requests, `date >= fromDate`.
 *
 * `tierFilter` is whatever subTierFilterFor() returned: a role string for
 * the tiers that see only their own board, a LIST of tiers for a board that
 * several tiers share (the Sr Staff board an Executive Teacher reads, Bobbie
 * 2026-09-28), or null for the roles that read every tier and must send NO
 * tier constraint at all. Passing a role a caller merely hopes is right is
 * how this goes wrong, so pass the value that function gave you rather than
 * one you assembled here.
 *
 * The filtered shapes, `==` and `in` alike, ride the (tier ASC, date ASC)
 * composite index in shared/firestore.indexes.json.
 */
export function buildSubBoardQuery(
  db: Firestore,
  tierFilter: SubTierFilter,
  fromDate: string
): Query<DocumentData> {
  return tierFilter
    ? query(
        collection(db, 'subRequests'),
        subTierWhere(tierFilter),
        where('date', '>=', fromDate),
        orderBy('date', 'asc')
      )
    : query(
        collection(db, 'subRequests'),
        where('date', '>=', fromDate),
        orderBy('date', 'asc')
      );
}

/**
 * One SEASON of requests, `fromDate <= date <= toDate`, for the Jr Staff
 * master report (PACKET S2).
 *
 * WHY A SECOND BUILDER RATHER THAN A WIDER FIRST ONE. buildSubBoardQuery above
 * is the LIVE BOARD's query and is deliberately upcoming-only; the report
 * looks BACKWARDS across a whole season, and quietly widening the board's
 * bound would have put a season of settled history on every staff member's
 * board. Two questions, two queries, one file.
 *
 * NO NEW INDEX. With `tierFilter` set this is (tier ASC, date ASC), which is
 * the composite index shared/firestore.indexes.json already carries for the
 * board itself; the second date bound rides the same range. With no tier
 * filter it is a bare date range, which needs none. Do NOT add an orderBy on
 * anything but date here or that stops being true.
 */
export function buildSubRangeQuery(
  db: Firestore,
  tierFilter: SubTierFilter,
  fromDate: string,
  toDate: string
): Query<DocumentData> {
  return tierFilter
    ? query(
        collection(db, 'subRequests'),
        subTierWhere(tierFilter),
        where('date', '>=', fromDate),
        where('date', '<=', toDate),
        orderBy('date', 'asc')
      )
    : query(
        collection(db, 'subRequests'),
        where('date', '>=', fromDate),
        where('date', '<=', toDate),
        orderBy('date', 'asc')
      );
}
